"use client";

import useLocalStorage, { writeStorage } from "@rehooks/local-storage";
import { createContext, Dispatch, SetStateAction, useCallback, useContext, useEffect, useReducer, useState } from "react";
import { STORAGE_KEY_CART } from "../utils/constants/storageKeys";
import CartStateDto from "@/projectRoot/api-client/interfaces/models/cart/CartState.dto";
import CartDto, { CartStatus } from "@/projectRoot/api-client/interfaces/models/cart/Cart.dto";
import CartCampaignDto from "@/projectRoot/api-client/interfaces/models/cart/CartCampaign.dto";
import { useApiClient } from "./ApiClientContext";
import { CartItemStatus, CartItemTemplates } from "@/projectRoot/api-client/interfaces/models/cart/CartItem.dto";
import { usePathname } from "next/navigation";

interface CartContext {
    cartState?: CartStateDto;
    cart?: CartDto;
    cartDispatchAction: Dispatch<CartAction>;
    showCartShortcut: Dispatch<SetStateAction<boolean>>;
    cartShortcut: boolean;
}

export enum QuantityAction {
    ADD = 'add',
    REMOVE = 'remove',
    INPUT = 'input',
}

export const defaultCartContext: CartContext = {
    cartDispatchAction: (value: CartAction) => null,
    cartShortcut: false,
    showCartShortcut: () => {},
}

export const CartContext = createContext(defaultCartContext);

export type CartProps = Partial<CartContext> & {
    children?: any,
}

const initialState: CartStateDto = {
    products: [],
}

type CartAction =
    | { type: "addProduct", payload: { 
        product_id: string, 
        quantity: number,
        stock_variant: string;
    } }
    | { type: "handleProductQuantity", payload: { 
        product_id: string, 
        quantity: number,
        stock_variant: string;
        type: QuantityAction, 
    } }
    | { type: "removeProduct", payload: {
        product_id: string;
        stock_variant: string;
    } }
    | { type: "setState", payload: CartStateDto }
    | { type: "setInitialState" }

function reducer(state: CartStateDto, action: CartAction): CartStateDto {
    let currentState: CartStateDto = state;

    switch(action.type) {
        case "addProduct": {
            let existingProduct = currentState.products.find(prod => prod.product_id == action.payload.product_id && prod.stock_variant == action.payload.stock_variant);
            if(existingProduct){
                existingProduct.quantity = existingProduct.quantity + action.payload.quantity;
                currentState = {...currentState};
            }else{
                currentState = {...currentState, products: [
                    ...currentState.products, 
                    {
                        product_id: action.payload.product_id,
                        quantity: action.payload.quantity,
                        stock_variant: action.payload.stock_variant
                    }
                ]};
            }
            writeStorage(STORAGE_KEY_CART, currentState);

            return {...currentState};

        };
        case "handleProductQuantity": {
            let product = currentState.products.find(item => item.product_id == action.payload.product_id && item.stock_variant == action.payload.stock_variant);
            if(product){
                switch(action.payload.type){
                    case QuantityAction.ADD: {
                        product.quantity = product.quantity + action.payload.quantity;
                        break;
                    }
                    case QuantityAction.REMOVE: {
                        product.quantity = product.quantity - action.payload.quantity;
                        if(product.quantity == 0) product.quantity = 1;
                        break;
                    }
                    case QuantityAction.INPUT: {
                        product.quantity = product.quantity + action.payload.quantity;
                        break;
                    }
                    default: {
                        break;
                    }
                }
            }
            writeStorage(STORAGE_KEY_CART, currentState);
            return {...currentState};
        }
        case "removeProduct": {
            let newState = currentState.products.filter(item => item.product_id !== action.payload.product_id || item.stock_variant !== action.payload.stock_variant);
            currentState = {...currentState, products: newState};
            writeStorage(STORAGE_KEY_CART, currentState);
            return {...currentState};
        }
        case "setState": {
            currentState = action.payload;
            return {...currentState};
        }
        case "setInitialState": {
            currentState = initialState;
            writeStorage(STORAGE_KEY_CART, currentState);
            return {...currentState};
        }
        default: {
            return state;
        }
    }

}

const CartOrderingProvider = ({children}: CartProps) => {
    const localStorageValue = useLocalStorage(STORAGE_KEY_CART)[0];
    const [state, dispatch] = useReducer(reducer, initialState);
    const {apiClient} = useApiClient();
    const [data, setData] = useState<CartDto | undefined>();
    const [showCartShortcut, setShowCartShortcut] = useState<boolean>(false);
    const pathname = usePathname();
    
    const fetchCart = useCallback(async () => {
    //    console.log('poziva se: ', state);
       if(state.products.length == 0){
        setData(undefined);
        return;
       };
        try{
            const response = await apiClient.webshopShoppingCart().processed(state);
            // console.log('req: ', state.products.map(prod => prod.product_id));
            if(response.data){
                setData({...response.data, items_count: response.data.items.reduce((a, b) => a + b.quantity, 0)});
                // console.log('res: ', response.data)
            }
        }catch(err){
            console.log(err);
        }
    }, [state]);

    useEffect(() => {
        if(!localStorageValue){
            dispatch({type: 'setInitialState'});
        }
    }, []);

    useEffect(() => {

        fetchCart();

    }, [state]);

    useEffect(() => {
        setShowCartShortcut(false);
    }, [pathname]);

    useEffect(() => {
        dispatch({type: 'setState', payload: localStorageValue as unknown as CartStateDto});
    }, [localStorageValue]);

  return (
    <CartContext.Provider value={{
        cartState: state,
        cart: data,
        cartDispatchAction: dispatch,
        cartShortcut: showCartShortcut,
        showCartShortcut: setShowCartShortcut,
    }}>
        {children}
    </CartContext.Provider>
  )
};

export default CartOrderingProvider;


export function useCart(): CartProps {
    return useContext<CartProps>(CartContext);
}